import * as React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { whyTdcNextOnVeraPage } from "@/content/why-tdc-nextonvera";
import {
  ALL_CATEGORIES_FILTER,
  BREAKTHROUGH_YEAR_END,
  BREAKTHROUGH_YEAR_START,
  getBreakthroughDataForCategory,
  getBreakthroughStackedData,
  getCategoryTotal,
  tdcBreakthroughCategories,
  tdcBreakthroughCategoryColors,
  tdcBreakthroughTotal,
} from "@/content/tdc-breakthroughs";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

const page = whyTdcNextOnVeraPage.breakthroughs;

type TooltipEntry = {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
  color?: string;
};

function BreakthroughTooltip({
  active,
  payload,
  stacked,
}: {
  active?: boolean;
  payload?: ReadonlyArray<TooltipEntry & { payload: { year: number } }>;
  stacked?: boolean;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const year = payload[0]?.payload.year ?? 0;
  const label = (count: number) => (count === 1 ? "breakthrough" : "breakthroughs");

  if (!stacked) {
    const count = Number(payload[0]?.value ?? 0);
    return (
      <div className="border-border bg-popover text-popover-foreground rounded-md border px-3 py-2 text-sm shadow-sm">
        <p className="font-display font-semibold tabular-nums">{year}</p>
        <p className="text-muted-foreground mt-0.5">
          {count} {label(count)}
        </p>
      </div>
    );
  }

  const total = payload.reduce((sum, entry) => sum + Number(entry.value ?? 0), 0);
  const segments = payload
    .filter((entry) => Number(entry.value ?? 0) > 0)
    .sort((a, b) => Number(b.value ?? 0) - Number(a.value ?? 0));

  return (
    <div className="border-border bg-popover text-popover-foreground max-w-64 rounded-md border px-3 py-2 text-sm shadow-sm">
      <p className="font-display font-semibold tabular-nums">
        {year} · {total} {label(total)}
      </p>
      <ul className="mt-1.5 space-y-1">
        {segments.map((entry) => (
          <li key={String(entry.dataKey)} className="flex items-center gap-1.5 text-xs">
            <span
              aria-hidden="true"
              className="inline-block size-2 shrink-0 rounded-full"
              style={{ backgroundColor: entry.color ?? "var(--primary)" }}
            />
            <span className="min-w-0 flex-1 truncate">{entry.name ?? entry.dataKey}</span>
            <span className="text-muted-foreground tabular-nums">{Number(entry.value ?? 0)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function WhyTdcNextOnVeraBreakthroughs() {
  const reducedMotion = usePrefersReducedMotion();
  const [filter, setFilter] = React.useState<string>(ALL_CATEGORIES_FILTER);

  const isAll = filter === ALL_CATEGORIES_FILTER;
  const categoryLabel = isAll ? null : filter;
  const chartData = React.useMemo(
    () => getBreakthroughDataForCategory(categoryLabel),
    [categoryLabel],
  );
  const stackedData = React.useMemo(() => getBreakthroughStackedData(), []);
  const total = isAll ? tdcBreakthroughTotal : getCategoryTotal(filter);
  const peak = chartData.reduce<{ year: number; count: number }>(
    (best, p) => (p.count > best.count ? p : best),
    { year: BREAKTHROUGH_YEAR_START, count: 0 },
  );

  const chartLabel = `${total} ${isAll ? "" : filter + " "}breakthroughs by year, ${BREAKTHROUGH_YEAR_START} to ${BREAKTHROUGH_YEAR_END}, highlighted by cancer category. Peak year ${peak.year} with ${peak.count}.`;

  return (
    <section
      id={page.id}
      aria-labelledby="why-breakthroughs-heading"
      className="border-border/70 bg-card border-y px-4 py-16 sm:px-6 sm:py-20 lg:px-8"
    >
      <div className="mx-auto w-full max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-primary mb-4 text-xs font-semibold tracking-[0.24em] uppercase">
            {page.eyebrow}
          </p>
          <h2
            id="why-breakthroughs-heading"
            className="font-display text-foreground text-3xl font-semibold tracking-tight text-balance sm:text-4xl"
          >
            {page.heading}
          </h2>
          <p className="text-muted-foreground mt-6 text-base leading-relaxed sm:text-lg">
            {page.body}
          </p>
        </div>

        {/* Cancer-type filter */}
        <div className="mt-8 flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
          <label
            htmlFor="cancer-type-filter"
            className="text-muted-foreground text-xs font-medium tracking-[0.16em] uppercase"
          >
            Filter by cancer type
          </label>
          <select
            id="cancer-type-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border-border bg-background text-foreground focus-visible:ring-ring h-9 rounded-md border px-3 text-sm shadow-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            <option value={ALL_CATEGORIES_FILTER}>All cancers ({tdcBreakthroughTotal})</option>
            {tdcBreakthroughCategories.map((cat) => (
              <option key={cat.label} value={cat.label}>
                {cat.label} ({cat.counts.reduce((s, c) => s + c, 0)})
              </option>
            ))}
          </select>
        </div>

        <figure className="mt-12">
          <div
            className="h-[280px] w-full min-w-0 sm:h-[360px] lg:h-[400px]"
            role="img"
            aria-label={chartLabel}
          >
            <ResponsiveContainer width="100%" height="100%" minWidth={0} debounce={50}>
              <BarChart
                data={isAll ? stackedData : chartData}
                margin={{ top: 8, right: 12, bottom: 24, left: 12 }}
                barCategoryGap="18%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="year"
                  stroke="var(--muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  label={{
                    value: page.xAxisLabel,
                    position: "insideBottom",
                    offset: -2,
                    fill: "var(--muted-foreground)",
                    fontSize: 12,
                  }}
                />
                <YAxis
                  dataKey="count"
                  stroke="var(--muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  width={40}
                  label={{
                    value: page.yAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    offset: 8,
                    fill: "var(--muted-foreground)",
                    fontSize: 12,
                  }}
                />
                <Tooltip
                  cursor={{ fill: "var(--muted)", opacity: 0.45 }}
                  content={<BreakthroughTooltip stacked={isAll} />}
                />
                {isAll
                  ? tdcBreakthroughCategories.map((cat) => (
                      <Bar
                        key={cat.label}
                        dataKey={cat.label}
                        name={cat.label}
                        stackId="breakthroughs"
                        fill={tdcBreakthroughCategoryColors[cat.label] ?? "var(--primary)"}
                        isAnimationActive={!reducedMotion}
                        maxBarSize={48}
                      />
                    ))
                  : (
                      <Bar
                        dataKey="count"
                        name={filter}
                        fill={tdcBreakthroughCategoryColors[filter] ?? "var(--primary)"}
                        radius={[4, 4, 0, 0]}
                        isAnimationActive={!reducedMotion}
                        maxBarSize={48}
                      />
                    )}
                {isAll ? (
                  <Legend
                    iconSize={9}
                    wrapperStyle={{ fontSize: 11, paddingTop: 8, lineHeight: "18px" }}
                  />
                ) : null}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <figcaption className="text-muted-foreground mt-4 text-center text-xs tracking-wide">
            {isAll
              ? page.source
              : `${filter}: ${total} breakthroughs (${BREAKTHROUGH_YEAR_START}\u2013${BREAKTHROUGH_YEAR_END}).`}
          </figcaption>
        </figure>

        <table className="sr-only">
          <caption>
            {isAll ? "Published breakthroughs by year" : `${filter} breakthroughs by year`}
          </caption>
          <thead>
            <tr>
              <th scope="col">{page.xAxisLabel}</th>
              <th scope="col">{page.yAxisLabel}</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((point) => (
              <tr key={point.year}>
                <td>{point.year}</td>
                <td>{point.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
